import { useCallback, useEffect, useState } from "react";
import { Control, FieldValues, Path, PathValue, UseFormSetValue, useWatch } from "react-hook-form";
import { getApiConfig, getToken, isApiConfigInitialized, onApiConfigReady } from "../utils";
import { getV1SettingsConfigurations, setBaseUrl } from "../api-client/api.accounting/api";
import useApi from "./useApi";

interface UpdateMapping {
    [key: string]: string; // Form field name -> API config key
}

interface UseFormFieldUpdaterConfig<T extends FieldValues = any> {
    control?: Control<T>;
    setValue?: UseFormSetValue<T>;
    watch?: any;
    fieldToUpdate: UpdateMapping;
    companyField?: string;
    locationField?: string;
    departmentField?: string;
    fieldArrayName?: string;
    transformResponse?: (data: any) => any;
    customDependencies?: any[];
    getFieldState?: any;
    isUpdateAccount?: boolean;
}
export function initializeApiBaseUrls(): void {
    if (!isApiConfigInitialized()) {
        console.warn('API configuration not initialized yet. Base URLs will be set when configuration is available.');
        return;
    }

    const config = getApiConfig();
    const baseUrl = config.backendBaseUrl;

    setBaseUrl(`${baseUrl}/accounting`);
}

onApiConfigReady((config) => {
    const baseUrl = config.backendBaseUrl;
    setBaseUrl(`${baseUrl}/accounting`);
});


export const useFormFieldUpdater = <T extends FieldValues = any>({
    control,
    setValue,
    fieldToUpdate,
    transformResponse,
    watch,
    fieldArrayName,
    companyField = 'company_id',
    locationField = 'location_id',
    departmentField = 'department_id',
    customDependencies = [],
    getFieldState,
    isUpdateAccount = false
}: UseFormFieldUpdaterConfig<T>) => {
    const { callApi } = useApi();


    const [initialState, setInitialState] = useState<{
        filters: { [companyField]: any;[locationField]: any;[departmentField]: any };
        values: Record<string, any>;
    } | null>(null);
    const companyPath = fieldArrayName ? `${fieldArrayName}.${companyField}` : companyField;
    const locationPath = fieldArrayName ? `${fieldArrayName}.${locationField}` : locationField;
    const departmentPath = fieldArrayName ? `${fieldArrayName}.${departmentField}` : departmentField;
    const data = watch?.(`${fieldArrayName}`) || {};
    const isDirtyCompany = getFieldState?.(companyPath)?.isDirty;
    const isDirtyLocation = getFieldState?.(locationPath)?.isDirty;
    const isDirtyDepartment = getFieldState?.(departmentPath)?.isDirty;
    const selectedCompany = data?.[companyField] || null;
    const selectedLocation = data?.[locationField] || null;
    const selectedDepartment = data?.[departmentField] || null;
    useEffect(() => {
        if (watch && !initialState) {
            const filters = {
                [companyField]: selectedCompany,
                [locationField]: selectedLocation,
                [departmentField]: selectedDepartment,
            };
            const values: Record<string, any> = {};
            Object.keys(fieldToUpdate).forEach((key) => {
                const path = fieldArrayName ? `${fieldArrayName}.${key}` : key;
                values[key] = watch(path as any);
            });

            setInitialState({ filters, values });
        }
    }, [watch, selectedCompany, selectedLocation, selectedDepartment, initialState, companyPath, locationPath, departmentPath, isDirtyCompany, isDirtyLocation, isDirtyDepartment]);

    const updateFields = useCallback((data: any, forceUpdate: boolean = false) => {
        if (!setValue || !data) return;

        const transformed = transformResponse ? transformResponse(data) : data;
        if (!transformed) return;

        Object.entries(fieldToUpdate).forEach(([sourceField, targetField]: any) => {
            const updateValue = transformed?.find((i: any) => i?.config_key == (typeof targetField == 'object' ? targetField.key : targetField));
            if (updateValue !== undefined && updateValue !== null) {
                const path = fieldArrayName ? `${fieldArrayName}.${sourceField}` : sourceField;

                // Check if field already has a value - skip update only if NOT force updating (edit mode protection)
                if (!forceUpdate) {
                    const existingValue = watch?.(path as any);
                    if (existingValue !== undefined && existingValue !== null && existingValue !== '') {
                        return; // Skip updating fields that already have values
                    }
                }

                if (typeof targetField == 'object') {
                    targetField.value(updateValue?.reference_id || updateValue?.reference_text);
                } else {
                    setValue(path as any, updateValue?.reference_id || updateValue?.reference_text);
                }
            }
        });
    }, [setValue, fieldToUpdate, transformResponse, fieldArrayName, watch, ...customDependencies]);



    const fetchAndUpdate = useCallback(async () => {
        if (!selectedCompany || !initialState) return;
        // Helper to extract ID from array/object values
        const getFilterValue = (val: any) => {
            if (Array.isArray(val)) {
                if (val.length === 0) return null;
                const firstVal = val[0];
                if (firstVal && typeof firstVal === 'object') return firstVal.id || firstVal.value || null;
                return firstVal || null;
            }
            if (val && typeof val === 'object') return val.id || val.value || null;
            return val || null;
        };

        const companyId = getFilterValue(selectedCompany);
        const locationId = getFilterValue(selectedLocation);
        const departmentId = getFilterValue(selectedDepartment);

        if (!companyId || !initialState) return;

        const initialFilters = {
            companyId: getFilterValue(initialState.filters[companyField]),
            locationId: getFilterValue(initialState.filters[locationField]),
            departmentId: getFilterValue(initialState.filters[departmentField]),
        };

        const isFilterChanged =
            initialFilters.companyId !== companyId ||
            initialFilters.locationId !== locationId ||
            initialFilters.departmentId !== departmentId;

        const isTargetFieldsEmpty = Object.keys(fieldToUpdate).every((key) => {
            const path = fieldArrayName ? `${fieldArrayName}.${key}` : key;
            const val = watch?.(path as any);
            return val === undefined || val === null || val === "";
        });

        if (!isFilterChanged && !isUpdateAccount && !isTargetFieldsEmpty) {
            Object.entries(initialState.values).forEach(([key, value]) => {
                const path = fieldArrayName ? `${fieldArrayName}.${key}` : key;
                setValue?.(path as any, value as any);
            });
            return;
        }

        const filter = `(company_id.eq=${companyId || 1}&location_id.eq=${locationId || null}&department_id.eq=${departmentId || null})`;

        // In edit mode (isUpdateAccount=true): only force update if user manually changed filter (isDirty)
        // In add mode: force update when filter changes
        const isUserChangedFilter = isDirtyCompany || isDirtyLocation || isDirtyDepartment;
        const shouldForceUpdate = isUpdateAccount
            ? (isFilterChanged && isUserChangedFilter)  // Edit mode: only when user changed
            : isFilterChanged;  // Add mode: any filter change

        callApi({
            apiFunc: () => getV1SettingsConfigurations({
                ...getToken(),
                filters: filter
            }),
            onSuccess: (res: any) => {
                updateFields(res?.data?.configuration_list, shouldForceUpdate);
            },
            onError: (err) => {
                console.error('Error fetching accounting settings:', err);
            }
        });
    }, [selectedCompany, selectedLocation, selectedDepartment, initialState, callApi, setValue, isDirtyCompany, isDirtyLocation, isDirtyDepartment, isUpdateAccount]);

    useEffect(() => {
        fetchAndUpdate();
    }, [fetchAndUpdate]);

    return {
        selectedCompany,
        selectedLocation,
        selectedDepartment
    };
};

export const useAccountingSetting = ({
    fieldToUpdate,
    fieldArrayName,
    companyField = 'company_id',
    locationField = 'location_id',
    departmentField = 'department_id',
    formMethods,
    customDependencies = [],
    ...rest
}: {
    fieldToUpdate: any;
    fieldArrayName?: string;
    companyField?: string;
    locationField?: string;
    departmentField?: string;
    setValue?: any;
    control?: any;
    formMethods?: any;
    customDependencies?: any[];
}) => {
    const { setValue, control, watch, getFieldState } = formMethods || {};

    return useFormFieldUpdater({
        control,
        setValue,
        watch,
        companyField,
        locationField,
        departmentField,
        fieldToUpdate,
        fieldArrayName,
        customDependencies,
        getFieldState,
        ...rest
    });
};